/**
 * The main toolbar's item model — one descriptor shape feeding BOTH the
 * primary row and the "More ▾" menu (task B11).
 *
 * Contract notes for the tasks built on top of this model:
 *  - `ToolbarItemId` is a STABLE, persisted identity. B14 stores these ids in
 *    `appStore.toolbarCustomization`, so an id is never renamed once shipped;
 *    a removed item's id stays reserved. Ids are deliberately decoupled from
 *    `testId` (which e2e specs pin) and from `commandId` (which the command
 *    registry owns) so the three can move independently.
 *  - Ordering lives in `ToolbarLayout`, NOT in the descriptors. B15 changes
 *    the default order by editing `DEFAULT_TOOLBAR_LAYOUT` alone; B14 feeds a
 *    user layout into `buildMainToolbarModel` and drops hidden ids. No call
 *    site moves for either.
 *  - `anchor` / `hideable` carry the anchoring rules B14 must honour (Home
 *    first, More ▾ last and never hideable). They are declarative metadata
 *    here; nothing reads them yet.
 *  - `commandId` links an item to the command registry, which is the single
 *    source of truth for "this action exists". `resolveToolbarAction` will
 *    dispatch through `executeCommand` for any descriptor that declares a
 *    command instead of a `run`.
 */
import type { LucideIcon } from 'lucide-react';
import type { ContextState } from '../../toolbar-context';
import type { BetaFeatureId } from '../../../core/beta';

/** Stable, persisted item identity. Never rename a shipped id. */
export type ToolbarItemId =
  | 'home'
  | 'new-query'
  | 'table-explorer'
  | 'terminal'
  | 'ai'
  | 'schema-overview'
  | 'visual-query'
  | 'dashboards'
  | 'more'
  | 'new-notebook'
  | 'open-notebook'
  | 'open-sql-file'
  | 'open-execution-plan'
  | 'deadlock-analyzer'
  | 'table-designer'
  | 'table-explorer-more'
  | 'data-profile'
  | 'meta-info-manager'
  | 'spatial-explorer'
  | 'schema-compare'
  | 'data-compare'
  | 'import-data'
  | 'database-blueprint'
  | 'session-browser'
  | 'tablespace-manager'
  | 'package-explorer'
  | 'filegroup-manager'
  | 'query-store'
  | 'agent-jobs'
  | 'instance-monitor'
  | 'data-pump';

/** Items that own a click handler — `ai` and `more` render their own surface. */
export type ToolbarActionId = Exclude<ToolbarItemId, 'ai' | 'more'>;

export type ToolbarMoreGroupId =
  | 'query'
  | 'notebooks'
  | 'open-file'
  | 'tables'
  | 'database'
  | 'compare'
  | 'dba';

/** How an item paints: a primary-row button, a menu row, or its own component. */
export type ToolbarItemRender = 'button' | 'menu-item' | 'custom';

export type ToolbarItemDescriptor = {
  id: ToolbarItemId;
  render: ToolbarItemRender;
  /** Visible label. Dynamic for Agent Jobs (engine-specific wording). */
  label: string;
  /** `data-testid` — pinned by e2e specs; never changes with placement. */
  testId: string;
  icon?: LucideIcon;
  /** Native `title` attribute. Omitted entirely when undefined. */
  tooltip?: string;
  /** Trailing muted note inside a menu row ("Not supported"). */
  note?: string;
  /** Rendered but inert. */
  disabled?: boolean;
  /** `false` removes the item from the DOM (engine / connection gating). */
  visible?: boolean;
  className?: string;
  /** Primary-row button with no label (Home). */
  iconOnly?: boolean;
  /** Placement the user may not override (B14). */
  anchor?: 'first' | 'last';
  /** `false` = B14 must not offer to hide this item. Defaults to true. */
  hideable?: boolean;
  /** Registry link. `null` when no command covers this item — see the map. */
  commandId: string | null;
  /** The action. Omit to dispatch `commandId` through the command registry. */
  run?: () => void;
};

export type ToolbarMoreGroup = {
  id: ToolbarMoreGroupId;
  label: string;
  items: ToolbarItemDescriptor[];
};

/**
 * Ordering only — the single thing B14 rewrites and B15 re-defaults.
 *
 * A stored layout is a snapshot of what the user decided about the items that
 * existed *when they customized*, so it is always merged over
 * `DEFAULT_TOOLBAR_LAYOUT` before rendering (see `mergeToolbarLayout`). That
 * merge is why `hidden` has to be explicit rather than implied by omission:
 * "the user hid this" and "this shipped after the user customized" are
 * different answers, and only the second is re-inserted.
 */
export type ToolbarLayout = {
  primary: ToolbarItemId[];
  more: Array<{ group: ToolbarMoreGroupId; items: ToolbarItemId[] }>;
  /** Ids the user deliberately hid. Never re-inserted by the merge. */
  hidden?: ToolbarItemId[];
};

export type MainToolbarModel = {
  primary: ToolbarItemDescriptor[];
  more: ToolbarMoreGroup[];
};

/** Everything the descriptors need to decide labels, tooltips and gating. */
export type ToolbarGating = {
  contextState: ContextState;
  firstConnectCoachVisible: boolean;
  browserMode: boolean;
  /** A connection the click handlers can resolve without a tree click (INJ-2). */
  hasFallbackConnection: boolean;
  notebooksUnsupported: boolean;
  schemaOverviewUnsupported: boolean;
  spatialExplorerUnsupported: boolean;
  /**
   * Why Import Data cannot open on the connection it resolves to (Kusto, Azure Storage: no SQL table to import into);
   * `undefined` when it can. Disables the item with this tooltip and a "Not supported" note.
   */
  importDataUnsupportedReason: string | undefined;
  /**
   * S1 Task 1.16 — the selected connection's engine declares no Query Editor /
   * no Table Explorer (`workspaceFeatures.supportsQueryEditor` /
   * `supportsTableExplorer`; false only for `azurestorage` today). The items
   * stay on the bar and are disabled with a why-tooltip, like Schema Overview.
   */
  queryEditorUnsupported: boolean;
  tableExplorerUnsupported: boolean;
  /** Engine of the selected connection — names it in the two tooltips above. */
  selectedConnectionEngine?: string;
  hasDbaEligibleConnection: boolean;
  hasAnyOracleConnection: boolean;
  hasAnyMssqlConnection: boolean;
  hasJobScheduler: boolean;
  jobSchedulerMenuLabel: string;
  /**
   * Resolved beta opt-ins (Settings → Beta Features). A disabled feature's
   * item sets `visible: false`, which drops it from the row / menu and marks
   * its customization row unavailable — the same mechanism the engine gates
   * already use, so a beta item can't escape by being promoted or reordered.
   */
  betaEnabled: Record<BetaFeatureId, boolean>;
};

export type ToolbarActions = Record<ToolbarActionId, () => void>;

export type ToolbarModelDeps = {
  gating: ToolbarGating;
  actions: ToolbarActions;
};

/**
 * B14's anchor policy, declared once so the renderer, the customization
 * normalizer and the settings-bundle importer cannot drift. Cross-checked
 * against the descriptors' `anchor`/`hideable` in `toolbarLayoutValidation`.
 */
export const TOOLBAR_MORE_ITEM_ID: ToolbarItemId = 'more';
export const TOOLBAR_ANCHOR_FIRST_ID: ToolbarItemId = 'home';
export const TOOLBAR_ANCHOR_LAST_ID: ToolbarItemId = TOOLBAR_MORE_ITEM_ID;
/** Hiding either anchor is refused: hiding More ▾ strands every menu item. */
export const UNHIDEABLE_TOOLBAR_ITEM_IDS: readonly ToolbarItemId[] = [
  TOOLBAR_ANCHOR_FIRST_ID,
  TOOLBAR_ANCHOR_LAST_ID,
];

export const MORE_GROUP_LABELS: Record<ToolbarMoreGroupId, string> = {
  query: 'Query',
  notebooks: 'Notebooks',
  'open-file': 'Open File',
  tables: 'Tables',
  database: 'Database',
  compare: 'Compare',
  dba: 'DBA',
};

/**
 * The shipped default order. B15's reshuffle is an edit to this constant and
 * nothing else — moving an id from a `more` group into `primary` promotes it.
 */
export const DEFAULT_TOOLBAR_LAYOUT: ToolbarLayout = {
  primary: ['home', 'new-query', 'table-explorer', 'terminal', 'ai', 'schema-overview', 'dashboards', 'more'],
  more: [
    { group: 'query', items: ['visual-query'] },
    { group: 'notebooks', items: ['new-notebook', 'open-notebook'] },
    { group: 'open-file', items: ['open-sql-file', 'open-execution-plan', 'deadlock-analyzer'] },
    { group: 'tables', items: ['table-designer', 'table-explorer-more', 'data-profile'] },
    { group: 'database', items: ['meta-info-manager', 'spatial-explorer'] },
    { group: 'compare', items: ['schema-compare', 'data-compare', 'import-data', 'database-blueprint'] },
    {
      group: 'dba',
      items: [
        'session-browser',
        'tablespace-manager',
        'package-explorer',
        'filegroup-manager',
        'query-store',
        'agent-jobs',
        'instance-monitor',
        'data-pump',
      ],
    },
  ],
};
